import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, CheckCircle, XCircle, Trash2, Users, Car, Calendar, User, Image, Video, Eye, Filter, Clock, ThumbsUp, ThumbsDown, Plus, Edit, BarChart3, Target, AlertTriangle, GitMerge, X, MapPin, Upload } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { useStats } from '../contexts/StatsContext';
import MediaLightbox from '../components/MediaLightbox';
import { AD_POSITIONS, getAdPositionMeta } from '../utils/adFormats';

const OcrResolveForm = ({ car, onResolve }) => {
  const [plate, setPlate] = useState(
    (car.suggestedPlates && car.suggestedPlates[0]) || ''
  );
  const [busy, setBusy] = useState(false);

  const submit = async (reject = false) => {
    if (!reject && !plate.trim()) {
      alert('Укажите номер автомобиля');
      return;
    }
    setBusy(true);
    try {
      await onResolve(car._id, plate.trim(), reject);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 8,
      padding: 12,
      background: 'var(--paper-2)',
      border: '1px solid var(--line)',
      borderRadius: 'var(--radius)'
    }}>
      <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--mute)' }}>
        OCR уверенность: {Math.round((car.ocrConfidence || 0) * 100)}%
      </p>
      {car.suggestedPlates?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {car.suggestedPlates.map((p) => (
            <button
              key={p}
              type="button"
              className="admin-pill admin-pill--count"
              style={{ cursor: 'pointer', border: 'none' }}
              onClick={() => setPlate(p)}
            >
              {p}
            </button>
          ))}
        </div>
      )}
      <input
        className="form-input"
        value={plate}
        onChange={(e) => setPlate(e.target.value.toUpperCase())}
        placeholder="01A123AA"
        style={{ marginBottom: 10 }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-primary" style={{ flex: 1, padding: '10px 12px', fontSize: 13 }} disabled={busy} onClick={() => submit(false)}>
          В модерацию
        </button>
        <button type="button" className="btn btn-danger" style={{ padding: '10px 12px', fontSize: 13 }} disabled={busy} onClick={() => submit(true)}>
          Отклонить
        </button>
      </div>
    </div>
  );
};

const AdminPanel = ({ user, onLogout }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCar, setSelectedCar] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showWatermarkModal, setShowWatermarkModal] = useState(false);
  const [watermarkSettings, setWatermarkSettings] = useState({
    applyWatermark: true,
    watermarkText: 'Avto-Tarix.uz',
    watermarkPosition: 'bottom-right',
    watermarkOpacity: 0.55
  });
  const { refreshStats } = useStats();
  
  // Реклама
  const [ads, setAds] = useState([]);
  const [showAdForm, setShowAdForm] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [newAd, setNewAd] = useState({
    title: '',
    link: '',
    position: 'left',
    priority: 0,
    startDate: '',
    endDate: '',
    isActive: true,
    mediaType: 'image',
    betweenSettings: {
      frequency: 3,
      startPosition: 1,
      maxPerPage: 5
    }
  });
  const [adMedia, setAdMedia] = useState(null);
  const [adSortBy, setAdSortBy] = useState('startDate');
  const [adSortDir, setAdSortDir] = useState('desc');
  const [adStatusFilter, setAdStatusFilter] = useState('all');

  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrSubmitBusy, setOcrSubmitBusy] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrPlate, setOcrPlate] = useState('');
  const [ocrDescription, setOcrDescription] = useState('');
  const [ocrPreviews, setOcrPreviews] = useState([]);
  const [ocrReusePhotos, setOcrReusePhotos] = useState([]);
  const [ocrReuseVideos, setOcrReuseVideos] = useState([]);
  const [ocrDragOver, setOcrDragOver] = useState(false);
  const ocrDropRef = useRef(null);
  const ocrDescRef = useRef('');

  const navigate = useNavigate();

  // Keep latest description for OCR request without stale closure
  useEffect(() => {
    ocrDescRef.current = ocrDescription;
  }, [ocrDescription]);

  const clearOcrForm = (opts = {}) => {
    const keepDescription = !!opts.keepDescription;
    setOcrPreviews((prev) => {
      prev.forEach((p) => {
        if (p.url && String(p.url).startsWith('blob:')) URL.revokeObjectURL(p.url);
      });
      return [];
    });
    setOcrResult(null);
    setOcrError(null);
    setOcrPlate('');
    if (!keepDescription) setOcrDescription('');
    setOcrReusePhotos([]);
    setOcrReuseVideos([]);
  };

  const extractUrlsFromHtml = (html) => {
    if (!html) return [];
    const urls = [];
    const re = /<img[^>]+src=["']([^"']+)["']/gi;
    let m;
    while ((m = re.exec(html)) !== null) {
      if (m[1] && !m[1].startsWith('data:')) urls.push(m[1]);
    }
    return urls;
  };

  const collectDroppedMedia = async (dataTransfer) => {
    const files = [];
    const urls = [];

    if (dataTransfer?.files?.length) {
      Array.from(dataTransfer.files).forEach((f) => {
        if (f.type.startsWith('image/') || f.type.startsWith('video/') || !f.type) {
          files.push(f);
        }
      });
    }

    if (dataTransfer?.items) {
      for (const item of Array.from(dataTransfer.items)) {
        if (item.kind === 'file') {
          const f = item.getAsFile();
          if (f && (f.type.startsWith('image/') || f.type.startsWith('video/'))) {
            if (!files.some((x) => x.name === f.name && x.size === f.size)) files.push(f);
          }
        }
      }
    }

    const uriList = dataTransfer?.getData?.('text/uri-list') || '';
    uriList.split('\n').map((s) => s.trim()).filter((s) => s && !s.startsWith('#')).forEach((u) => urls.push(u));

    const plain = (dataTransfer?.getData?.('text/plain') || '').trim();
    if (/^https?:\/\//i.test(plain)) urls.push(plain);

    extractUrlsFromHtml(dataTransfer?.getData?.('text/html') || '').forEach((u) => urls.push(u));

    const uniqueUrls = [...new Set(urls.filter((u) => /^https?:\/\//i.test(u)))];

    return { files, urls: uniqueUrls };
  };

  const runOcrMedia = async ({ files = [], urls = [], previewItems = null } = {}) => {
    if (!files.length && !urls.length) return;

    clearOcrForm({ keepDescription: true });
    setOcrBusy(true);
    setOcrError(null);
    if (previewItems?.length) {
      setOcrPreviews(previewItems);
    } else if (files.length) {
      setOcrPreviews(files.map((file) => ({
        name: file.name,
        type: file.type || 'image/jpeg',
        url: URL.createObjectURL(file)
      })));
    } else if (urls.length) {
      setOcrPreviews(urls.map((u, i) => ({
        name: `url-${i}`,
        type: 'image/jpeg',
        url: u
      })));
    }

    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      files.forEach((file) => {
        if (file.type.startsWith('video/')) form.append('videos', file);
        else form.append('photos', file);
      });
      if (urls.length) form.append('imageUrls', urls.join('\n'));
      if (ocrDescRef.current.trim()) form.append('description', ocrDescRef.current.trim());

      const { data } = await axios.post(`${config.API_URL}/api/admin/ocr-test`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOcrResult(data);
      setOcrReusePhotos(data.files?.photos || []);
      setOcrReuseVideos(data.files?.videos || []);
      const best =
        data.plate ||
        (data.suggestedPlates && data.suggestedPlates[0]) ||
        '';
      setOcrPlate(best);
    } catch (err) {
      setOcrError(err.response?.data?.error || err.message);
    } finally {
      setOcrBusy(false);
    }
  };

  const runOcrOnFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    await runOcrMedia({ files });
  };

  const onOcrDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOcrDragOver(false);
    if (ocrBusy || ocrSubmitBusy) return;
    const { files, urls } = await collectDroppedMedia(e.dataTransfer);
    if (!files.length && !urls.length) {
      setOcrError('Перетащите изображение (файл или картинку со страницы)');
      return;
    }
    await runOcrMedia({ files, urls });
  };

  const onOcrPaste = async (e) => {
    if (ocrBusy || ocrSubmitBusy) return;
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) files.push(new File([f], `paste-${Date.now()}.png`, { type: f.type || 'image/png' }));
      }
    }
    const text = (e.clipboardData.getData('text/plain') || '').trim();
    const urls = /^https?:\/\//i.test(text) ? [text] : [];

    if (!files.length && !urls.length) return;
    e.preventDefault();
    await runOcrMedia({ files, urls });
  };

  const submitOcrCar = async (e) => {
    e.preventDefault();
    if (!ocrReusePhotos.length && !ocrReuseVideos.length) {
      alert('Сначала загрузите фото или видео');
      return;
    }
    if (!ocrPlate.trim()) {
      alert('Укажите номер автомобиля');
      return;
    }

    setOcrSubmitBusy(true);
    setOcrError(null);
    try {
      const token = localStorage.getItem('token');
      const form = new FormData();
      form.append('licensePlate', ocrPlate.trim());
      form.append('description', ocrDescription.trim());
      form.append('reusePhotos', ocrReusePhotos.join(','));
      form.append('reuseVideos', ocrReuseVideos.join(','));
      form.append('skipOcr', '1');
      if (ocrResult?.confidence) form.append('ocrConfidence', String(ocrResult.confidence));
      if (ocrResult?.suggestedPlates?.length) {
        form.append('suggestedPlates', ocrResult.suggestedPlates.join(','));
      }

      await axios.post(`${config.API_URL}/api/admin/cars/quick-add`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });

      clearOcrForm();
      await fetchCars();
      alert('Заявка отправлена на модерацию');
    } catch (err) {
      const data = err.response?.data;
      if (data?.suggestedPlates?.length && !ocrPlate.trim()) {
        setOcrPlate(data.suggestedPlates[0]);
      }
      setOcrError(data?.error || err.message);
    } finally {
      setOcrSubmitBusy(false);
    }
  };

  const getAdScheduleStatus = (ad) => {
    const now = Date.now();
    const start = ad.startDate ? new Date(ad.startDate).getTime() : 0;
    const end = ad.endDate ? new Date(ad.endDate).getTime() : Infinity;
    if (!ad.isActive) return 'inactive';
    if (now < start) return 'scheduled';
    if (now > end) return 'expired';
    return 'live';
  };

  const formatAdDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysLeftLabel = (ad) => {
    if (!ad.endDate) return 'без срока';
    const end = new Date(ad.endDate);
    const diffMs = end.getTime() - Date.now();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (days < 0) return `истекла ${Math.abs(days)} дн. назад`;
    if (days === 0) return 'последний день';
    return `ещё ${days} дн.`;
  };

  const getAdCtr = (ad) => {
    const views = ad.views || 0;
    const clicks = ad.clicks || 0;
    if (!views) return '0%';
    return `${((clicks / views) * 100).toFixed(1)}%`;
  };

  const positionLabel = (position) => {
    if (position === 'left' || position === 'desktop-left') return 'Слева';
    if (position === 'right' || position === 'desktop-right') return 'Справа';
    if (position === 'between') return 'Между постами';
    if (position === 'mobile') return 'Мобильная';
    return position || '—';
  };

  const sortedFilteredAds = [...ads]
    .filter((ad) => {
      if (adStatusFilter === 'all') return true;
      return getAdScheduleStatus(ad) === adStatusFilter;
    })
    .sort((a, b) => {
      const dir = adSortDir === 'asc' ? 1 : -1;
      const pick = (ad) => {
        switch (adSortBy) {
          case 'endDate':
            return new Date(ad.endDate || 0).getTime();
          case 'priority':
            return ad.priority || 0;
          case 'views':
            return ad.views || 0;
          case 'clicks':
            return ad.clicks || 0;
          case 'title':
            return (ad.title || '').toLowerCase();
          case 'createdAt':
            return new Date(ad.createdAt || 0).getTime();
          case 'startDate':
          default:
            return new Date(ad.startDate || 0).getTime();
        }
      };
      const av = pick(a);
      const bv = pick(b);
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * dir;
      }
      return (av - bv) * dir;
    });

  const liveAdsCount = ads.filter((ad) => getAdScheduleStatus(ad) === 'live').length;
  const scheduledAdsCount = ads.filter((ad) => getAdScheduleStatus(ad) === 'scheduled').length;
  const expiredAdsCount = ads.filter((ad) => getAdScheduleStatus(ad) === 'expired').length;

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Токен не найден. Пожалуйста, войдите в систему.');
      navigate(config.ADMIN_PANEL_PATH);
      return;
    }
    fetchCars();
    fetchAds();
  }, []);

  const fetchCars = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/api/admin/cars`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCars(response.data);
    } catch (error) {
      console.error('Ошибка загрузки записей:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/api/admin/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAds(response.data);
    } catch (error) {
      console.error('Ошибка загрузки рекламы:', error);
      if (error.response?.status === 403) {
        alert('Сессия истекла. Пожалуйста, войдите в систему заново.');
        localStorage.removeItem('token');
        navigate(config.ADMIN_PANEL_PATH);
      }
    }
  };

  const handleAdSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Токен не найден. Пожалуйста, войдите в систему заново.');
        navigate(config.ADMIN_PANEL_PATH);
        return;
      }
      const formData = new FormData();
      
      formData.append('title', newAd.title);
      formData.append('link', newAd.link);
      formData.append('position', newAd.position);
      formData.append('priority', newAd.priority);
      formData.append('startDate', newAd.startDate);
      formData.append('endDate', newAd.endDate);
      formData.append('mediaType', newAd.mediaType);
      formData.append('isActive', newAd.isActive.toString());
      
      // Добавляем настройки для рекламы между постами
      if (newAd.position === 'between') {
        formData.append('betweenSettings', JSON.stringify(newAd.betweenSettings));
      }
      
      if (adMedia) {
        formData.append('media', adMedia);
      }

      if (editingAd) {
        await axios.put(`${config.API_URL}/api/admin/ads/${editingAd._id}`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post(`${config.API_URL}/api/admin/ads`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      fetchAds();
      setShowAdForm(false);
      setEditingAd(null);
      setNewAd({
        title: '',
        link: '',
        position: 'left',
        priority: 0,
        startDate: '',
        endDate: '',
        isActive: true,
        mediaType: 'image',
        betweenSettings: {
          frequency: 3,
          startPosition: 1,
          maxPerPage: 5
        }
      });
      setAdMedia(null);
    } catch (error) {
      console.error('Ошибка сохранения рекламы:', error);
      if (error.response?.status === 403) {
        alert('Сессия истекла. Пожалуйста, войдите в систему заново.');
        localStorage.removeItem('token');
        navigate(config.ADMIN_PANEL_PATH);
      } else {
        alert(error.response?.data?.error || 'Ошибка сохранения рекламы');
      }
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту рекламу?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_URL}/api/admin/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAds();
    } catch (error) {
      console.error('Ошибка удаления рекламы:', error);
    }
  };

  const editAd = (ad) => {
    setEditingAd(ad);
    setNewAd({
      title: ad.title,
      link: ad.link,
      position: ad.position,
      priority: ad.priority,
      startDate: ad.startDate ? (() => {
        const d = new Date(ad.startDate);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      })() : '',
      endDate: ad.endDate ? (() => {
        const d = new Date(ad.endDate);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      })() : '',
      isActive: ad.isActive,
      mediaType: ad.mediaType || 'image',
      betweenSettings: ad.betweenSettings ? { ...ad.betweenSettings } : {
        frequency: 3,
        startPosition: 1,
        maxPerPage: 5
      }
    });
    setShowAdForm(true);
  };

  const moderateCar = async (carId, status, customWatermarkSettings = null) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { status };
      
      // При одобрении всегда передаём watermark (по умолчанию Avto-Tarix.uz)
      if (status === 'approved') {
        payload.watermarkSettings = customWatermarkSettings || {
          applyWatermark: true,
          watermarkText: 'Avto-Tarix.uz',
          watermarkPosition: 'bottom-right',
          watermarkOpacity: 0.55
        };
      }
      
      await axios.put(`${config.API_URL}/api/admin/cars/${carId}/moderate`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchCars();
      refreshStats();
      setSelectedCar(null);
      setShowWatermarkModal(false);
    } catch (error) {
      console.error('Ошибка модерации:', error);
    }
  };

  const deleteCar = async (carId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту запись?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${config.API_URL}/api/admin/cars/${carId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchCars();
      refreshStats(); // Обновляем статистику после удаления
      setSelectedCar(null);
    } catch (error) {
      console.error('Ошибка удаления:', error);
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/api/auth/create-admin`, newAdmin, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewAdmin({ username: '', password: '' });
      setShowCreateAdmin(false);
      alert('Администратор создан успешно!');
    } catch (error) {
      alert(error.response?.data?.error || 'Ошибка создания администратора');
    }
  };

  // Функции для работы с дубликатами
  const fetchDuplicates = async (carId) => {
    try {
      // Сначала устанавливаем selectedCar
      const mainCar = cars.find(car => car._id === carId);
      if (mainCar) {
        setSelectedCar(mainCar);
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.API_URL}/api/admin/cars/${carId}/duplicates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicates(response.data);
      setShowDuplicates(true);
    } catch (error) {
      console.error('Ошибка загрузки дубликатов:', error);
    }
  };

  const mergeCars = async (mainCarId, duplicateId) => {
    console.log('mergeCars вызвана с параметрами:', { mainCarId, duplicateId });
    
    if (!mainCarId || !duplicateId) {
      console.error('Ошибка: не указаны ID записей для объединения', { mainCarId, duplicateId });
      alert('Ошибка: не указаны ID записей для объединения');
      return;
    }
    
    try {
      console.log('Отправляем запрос на объединение...');
      const token = localStorage.getItem('token');
      console.log('Токен:', token ? 'есть' : 'нет');
      
      const response = await axios.post(`${config.API_URL}/api/admin/cars/${mainCarId}/merge`, {
        duplicateId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Ответ сервера:', response.data);
      alert('Записи успешно объединены!');
      fetchCars();
      setShowDuplicates(false);
      setSelectedCar(null);
    } catch (error) {
      console.error('Ошибка объединения записей:', error);
      console.error('Детали ошибки:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(error.response?.data?.error || 'Ошибка объединения записей');
    }
  };

  const rejectDuplicate = async (mainCarId, duplicateId, reason) => {
    if (!mainCarId || !duplicateId) {
      alert('Ошибка: не указаны ID записей для отклонения');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${config.API_URL}/api/admin/cars/${mainCarId}/reject-duplicate`, {
        duplicateId,
        reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Дубликат отклонен!');
      fetchCars();
      setShowDuplicates(false);
      setSelectedCar(null);
    } catch (error) {
      console.error('Ошибка отклонения дубликата:', error);
      alert(error.response?.data?.error || 'Ошибка отклонения дубликата');
    }
  };

  // Функции для работы с водными знаками
  const showWatermarkOptions = (carId) => {
    setSelectedCar(cars.find(car => car._id === carId));
    setShowWatermarkModal(true);
  };

  const handleWatermarkApproval = () => {
    if (selectedCar) {
      moderateCar(selectedCar._id, 'approved', watermarkSettings);
    }
  };

  const handleApprovalWithoutWatermark = () => {
    if (selectedCar) {
      moderateCar(selectedCar._id, 'approved', { applyWatermark: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'rejected': return 'status-rejected';
      case 'pending': return 'status-pending';
      case 'ocr_review': return 'status-ocr-review';
      default: return 'status-badge';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Одобрено';
      case 'rejected': return 'Отклонено';
      case 'pending': return 'На модерации';
      case 'ocr_review': return 'На проверке OCR';
      default: return 'Неизвестно';
    }
  };

  const resolveOcr = async (carId, licensePlate, reject = false) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${config.API_URL}/api/admin/cars/${carId}/ocr-resolve`,
        reject ? { reject: true } : { licensePlate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCars();
      if (selectedCar && selectedCar._id === carId) {
        setSelectedCar(null);
      }
    } catch (error) {
      console.error('OCR resolve error:', error);
      alert(error.response?.data?.error || 'Ошибка при обработке OCR');
    }
  };

  const filteredCars = cars.filter(car => 
    filterStatus === 'all' || car.status === filterStatus
  );

  const stats = {
    pending: cars.filter(car => car.status === 'pending').length,
    approved: cars.filter(car => car.status === 'approved').length,
    rejected: cars.filter(car => car.status === 'rejected').length,
    ocrReview: cars.filter(car => car.status === 'ocr_review').length,
    total: cars.length
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div>
            <div className="admin-loading__spinner" />
            <p>Загрузка панели администратора...</p>
          </div>
        </div>
      </div>
    );
  }

  const totalAdViews = ads.reduce((total, ad) => total + (ad.views || 0), 0);
  const totalAdClicks = ads.reduce((total, ad) => total + (ad.clicks || 0), 0);

  return (
    <div className="admin-page">
      <header className="admin-topbar">
        <div className="container">
          <div className="admin-topbar__inner">
            <div className="admin-topbar__brand">
              <div className="admin-topbar__mark">
                <Car size={22} />
              </div>
              <div>
                <h1 className="admin-topbar__title">Панель администратора</h1>
                <p className="admin-topbar__sub">
                  Добро пожаловать, <strong>{user.username}</strong>
                </p>
              </div>
            </div>
            <div className="admin-topbar__actions">
              <button type="button" onClick={() => setShowAdForm(true)} className="btn btn-primary">
                <Plus size={16} />
                Реклама
              </button>
              <button type="button" onClick={() => setShowCreateAdmin(true)} className="btn btn-secondary">
                <Users size={16} />
                Админ
              </button>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/';
                }}
                className="btn btn-secondary"
              >
                <Car size={16} />
                На сайт
              </button>
              <button type="button" onClick={onLogout} className="btn btn-danger">
                <LogOut size={16} />
                Выйти
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="admin-section" style={{ marginBottom: 20 }}>
          <div className="admin-section__head">
            <div>
              <h3 className="admin-section__title">Добавить через OCR</h3>
              <p className="admin-section__meta">
                Перетащите фото с сайта/ПК, вставьте Ctrl+V или выберите файлы — номер распознается сам
              </p>
            </div>
            {(ocrResult || ocrPreviews.length > 0) && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={ocrBusy || ocrSubmitBusy}
                onClick={clearOcrForm}
              >
                Сбросить
              </button>
            )}
          </div>

          <div className="admin-section__body" style={{ padding: '4px 0 18px' }}>
            {ocrError && (
              <p style={{ color: 'var(--danger)', margin: '0 22px 12px' }}>{ocrError}</p>
            )}

            <div
              ref={ocrDropRef}
              className={`admin-ocr-drop${ocrDragOver ? ' is-dragover' : ''}${ocrBusy ? ' is-busy' : ''}`}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOcrDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setOcrDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                if (!e.currentTarget.contains(e.relatedTarget)) setOcrDragOver(false);
              }}
              onDrop={onOcrDrop}
              onPaste={onOcrPaste}
              tabIndex={0}
              role="button"
              aria-label="Зона загрузки фото для OCR"
            >
              <Upload size={28} strokeWidth={1.75} />
              <div>
                <strong>{ocrBusy ? 'Распознаём номер…' : 'Перетащите картинку сюда'}</strong>
                <p>
                  С интернета, с рабочего стола или Ctrl+V. Можно также выбрать файлы.
                </p>
              </div>
              <label className="btn btn-secondary" style={{ cursor: ocrBusy ? 'wait' : 'pointer' }}>
                {ocrBusy ? 'Подождите…' : 'Выбрать файлы'}
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  style={{ display: 'none' }}
                  disabled={ocrBusy || ocrSubmitBusy}
                  onChange={runOcrOnFiles}
                />
              </label>
            </div>

            {ocrPreviews.length > 0 && (
              <div style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                margin: '14px 22px'
              }}>
                {ocrPreviews.map((p) => (
                  <div
                    key={p.url + p.name}
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid var(--line)',
                      background: 'var(--paper-2)'
                    }}
                  >
                    {String(p.type || '').startsWith('video/') ? (
                      <video src={p.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <img
                        src={p.url}
                        alt=""
                        referrerPolicy="no-referrer"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {ocrResult && !ocrError && (
              <p style={{ margin: '0 22px 12px', fontSize: 13, color: 'var(--mute)' }}>
                OCR: {ocrResult.plate || 'номер не уверен'}
                {' · '}
                {Math.round((ocrResult.confidence || 0) * 100)}%
                {ocrResult.needsReview ? ' · проверьте номер вручную' : ' · распознан уверенно'}
              </p>
            )}

            {(ocrResult || ocrReusePhotos.length > 0 || ocrReuseVideos.length > 0) && (
              <form onSubmit={submitOcrCar} className="admin-form-grid" style={{ gap: 14, margin: '0 22px' }}>
                <div>
                  <label className="admin-field-label" htmlFor="ocr-plate">Номер авто</label>
                  <input
                    id="ocr-plate"
                    type="text"
                    value={ocrPlate}
                    onChange={(e) => setOcrPlate(e.target.value.toUpperCase())}
                    placeholder="01A123BC"
                    required
                    style={{ width: '100%' }}
                  />
                  {ocrResult?.suggestedPlates?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {ocrResult.suggestedPlates.map((p) => (
                        <button
                          key={p}
                          type="button"
                          className="admin-pill admin-pill--count"
                          style={{ cursor: 'pointer', border: 'none' }}
                          onClick={() => setOcrPlate(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="admin-field-label" htmlFor="ocr-desc">Описание ДТП</label>
                  <textarea
                    id="ocr-desc"
                    value={ocrDescription}
                    onChange={(e) => setOcrDescription(e.target.value)}
                    placeholder="Место, дата, что произошло…"
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>

                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={ocrSubmitBusy || ocrBusy || !ocrPlate.trim()}
                  >
                    {ocrSubmitBusy ? 'Отправляем…' : 'Отправить на модерацию'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="admin-stats">
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--pending"><Clock size={22} /></div>
            <div>
              <p className="admin-stat__label">На модерации</p>
              <p className="admin-stat__value">{stats.pending}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--signal"><Eye size={22} /></div>
            <div>
              <p className="admin-stat__label">OCR проверка</p>
              <p className="admin-stat__value">{stats.ocrReview}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--ok"><ThumbsUp size={22} /></div>
            <div>
              <p className="admin-stat__label">Одобрено</p>
              <p className="admin-stat__value">{stats.approved}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--danger"><ThumbsDown size={22} /></div>
            <div>
              <p className="admin-stat__label">Отклонено</p>
              <p className="admin-stat__value">{stats.rejected}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--ink"><Car size={22} /></div>
            <div>
              <p className="admin-stat__label">Всего заявок</p>
              <p className="admin-stat__value">{stats.total}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--ink"><Target size={22} /></div>
            <div>
              <p className="admin-stat__label">Реклама</p>
              <p className="admin-stat__value">{ads.length}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--ok"><BarChart3 size={22} /></div>
            <div>
              <p className="admin-stat__label">В эфире</p>
              <p className="admin-stat__value">{liveAdsCount}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--signal"><Eye size={22} /></div>
            <div>
              <p className="admin-stat__label">Показы рекламы</p>
              <p className="admin-stat__value">{totalAdViews}</p>
            </div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat__icon admin-stat__icon--signal"><Target size={22} /></div>
            <div>
              <p className="admin-stat__label">Клики</p>
              <p className="admin-stat__value">{totalAdClicks}</p>
            </div>
          </div>
        </div>

        {/* Управление рекламой */}
        <div className="admin-section">
          <div className="admin-section__head">
            <div>
              <h3 className="admin-section__title">Управление рекламой</h3>
              <p className="admin-section__meta">
                Всего {ads.length} · в эфире {liveAdsCount} · запланировано {scheduledAdsCount} · истекло {expiredAdsCount}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowAdForm(true)}
              className="btn btn-primary"
            >
              <Plus size={16} />
              Добавить рекламу
            </button>

            {ads.length > 0 && (
              <div className="admin-section__tools">
                <label>
                  Статус
                  <select
                    className="admin-select"
                    value={adStatusFilter}
                    onChange={(e) => setAdStatusFilter(e.target.value)}
                  >
                    <option value="all">Все</option>
                    <option value="live">В эфире</option>
                    <option value="scheduled">Запланированы</option>
                    <option value="expired">Истекли</option>
                    <option value="inactive">Выключены</option>
                  </select>
                </label>

                <label>
                  Сортировка
                  <select
                    className="admin-select"
                    value={adSortBy}
                    onChange={(e) => setAdSortBy(e.target.value)}
                  >
                    <option value="startDate">По дате начала показа</option>
                    <option value="endDate">По дате окончания</option>
                    <option value="createdAt">По дате создания</option>
                    <option value="priority">По приоритету</option>
                    <option value="views">По просмотрам</option>
                    <option value="clicks">По кликам</option>
                    <option value="title">По названию</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ padding: '8px 12px', fontSize: 13 }}
                  onClick={() => setAdSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  title="Направление сортировки"
                >
                  <Clock size={14} />
                  {adSortDir === 'asc' ? 'Сначала старые / меньше' : 'Сначала новые / больше'}
                </button>
              </div>
            )}
          </div>

          {ads.length === 0 ? (
            <div className="admin-empty">
              <Target size={48} color="#6b7385" />
              <h3>Нет рекламных баннеров</h3>
              <p>Добавьте первый рекламный баннер для отображения на сайте</p>
              <button type="button" onClick={() => setShowAdForm(true)} className="btn btn-primary" style={{ marginTop: 16 }}>
                <Plus size={16} />
                Добавить рекламу
              </button>
            </div>
          ) : sortedFilteredAds.length === 0 ? (
            <div className="admin-empty">Нет рекламы по выбранному фильтру</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Реклама</th>
                    <th>Позиция</th>
                    <th>Статус</th>
                    <th>Период показа</th>
                    <th>Метрики</th>
                    <th>Приоритет</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredAds.map((ad) => {
                    const status = getAdScheduleStatus(ad);
                    const statusClass = {
                      live: 'admin-pill--live',
                      scheduled: 'admin-pill--scheduled',
                      expired: 'admin-pill--expired',
                      inactive: 'admin-pill--inactive'
                    }[status];
                    const statusLabel = {
                      live: 'В эфире',
                      scheduled: 'Запланирована',
                      expired: 'Истекла',
                      inactive: 'Выключена'
                    }[status];
                    const mediaSrc = ad.image || ad.video
                      ? `${config.API_URL}/uploads/${ad.image || ad.video}`
                      : null;

                    return (
                      <tr key={ad._id}>
                        <td>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            <div className="admin-thumb">
                              {mediaSrc ? (
                                ad.mediaType === 'video' ? (
                                  <Video size={20} />
                                ) : (
                                  <img src={mediaSrc} alt="" />
                                )
                              ) : (
                                <Image size={20} />
                              )}
                            </div>
                            <div>
                              <div className="admin-table__title">{ad.title}</div>
                              <a
                                href={ad.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-table__link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {ad.link}
                              </a>
                              <div className="admin-table__muted">
                                Создана: {formatAdDateTime(ad.createdAt)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {positionLabel(ad.position)}
                          {ad.position === 'between' && ad.betweenSettings && (
                            <div className="admin-table__muted">
                              каждые {ad.betweenSettings.frequency} · с {ad.betweenSettings.startPosition} · макс {ad.betweenSettings.maxPerPage}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`admin-pill ${statusClass}`}>{statusLabel}</span>
                          <div className="admin-table__muted">{getDaysLeftLabel(ad)}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <div><strong>С:</strong> {formatAdDateTime(ad.startDate)}</div>
                          <div style={{ marginTop: 4 }}><strong>До:</strong> {formatAdDateTime(ad.endDate)}</div>
                        </td>
                        <td>
                          <div>Просм.: {ad.views || 0}</div>
                          <div style={{ marginTop: 4 }}>Клики: {ad.clicks || 0}</div>
                          <div className="admin-table__muted">CTR {getAdCtr(ad)}</div>
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--ink)', fontSize: 16 }}>
                          {ad.priority ?? 0}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              onClick={() => editAd(ad)}
                              className="btn btn-secondary"
                              style={{ padding: '6px 10px', fontSize: 12 }}
                              title="Редактировать"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteAd(ad._id)}
                              className="btn btn-danger"
                              style={{ padding: '6px 10px', fontSize: 12 }}
                              title="Удалить"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Filters and Controls */}
        <div className="admin-section">
          <div className="admin-section__head">
            <div>
              <h3 className="admin-section__title">Заявки на модерацию</h3>
              <p className="admin-section__meta">{filteredCars.length} заявок в текущем фильтре</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Filter size={16} color="#6b7385" />
              <select
                className="admin-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Все заявки</option>
                <option value="pending">На модерации</option>
                <option value="ocr_review">На проверке OCR</option>
                <option value="approved">Одобренные</option>
                <option value="rejected">Отклоненные</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications Grid */}
        {filteredCars.length === 0 ? (
          <div className="admin-section">
            <div className="admin-empty">
              <Car size={48} color="#6b7385" />
              <h3>Нет заявок</h3>
              <p>
                {filterStatus === 'all' ? 'Пока нет заявок для модерации' : `Нет заявок со статусом "${getStatusText(filterStatus)}"`}
              </p>
            </div>
          </div>
        ) : (
          <div className="admin-cars">
            {filteredCars.map((car) => (
              <div key={car._id} className="car-card">
                <div className="car-card-header">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <h3 className="admin-car__plate">{car.licensePlate}</h3>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className={`status-badge ${getStatusColor(car.status)}`}>
                          {getStatusText(car.status)}
                        </span>
                        {car.duplicatesCount > 0 && (
                          <span className="admin-pill admin-pill--dup">
                            <AlertTriangle size={12} />
                            {car.duplicatesCount} дубликат{car.duplicatesCount > 1 ? 'ов' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="admin-car__actions">
                      {car.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={() => showWatermarkOptions(car._id)}
                            className="admin-icon-btn admin-icon-btn--ok"
                            title="Одобрить с настройками водного знака"
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moderateCar(car._id, 'rejected')}
                            className="admin-icon-btn admin-icon-btn--danger"
                            title="Отклонить"
                          >
                            <XCircle size={18} />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteCar(car._id)}
                        className="admin-icon-btn"
                        title="Удалить"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="car-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="meta-item">
                      <Calendar size={16} />
                      {new Date(car.createdAt).toLocaleDateString('ru-RU', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>

                    {car.submittedBy && (
                      <div className="meta-item">
                        <User size={16} />
                        От: {car.submittedBy}
                      </div>
                    )}

                    {car.externalSource === 'telegram_collector' && car.externalMetadata?.messageLink && (
                      <div className="meta-item">
                        <a href={car.externalMetadata.messageLink} target="_blank" rel="noopener noreferrer" className="admin-table__link">
                          Открыть пост в Telegram
                        </a>
                      </div>
                    )}

                    {car.description && (
                      <p style={{
                        fontSize: 14,
                        color: 'var(--slate)',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        margin: 0
                      }}>
                        {car.description}
                      </p>
                    )}

                    {car.status === 'ocr_review' && (
                      <OcrResolveForm
                        car={car}
                        onResolve={resolveOcr}
                      />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 4 }}>
                      {car.photos && car.photos.length > 0 && (
                        <div className="meta-item">
                          <Image size={16} />
                          {car.photos.length} фото
                        </div>
                      )}
                      {car.videos && car.videos.length > 0 && (
                        <div className="meta-item">
                          <Video size={16} />
                          {car.videos.length} видео
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="admin-car__foot">
                  <button
                    type="button"
                    onClick={() => setSelectedCar(car)}
                    className="btn btn-primary"
                  >
                    <Eye size={16} />
                    Подробный просмотр
                  </button>
                  {car.duplicatesCount > 0 && (
                    <button
                      type="button"
                      onClick={() => fetchDuplicates(car._id)}
                      className="btn btn-secondary"
                    >
                      <AlertTriangle size={16} />
                      Дубликаты
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Car Detail Modal */}
      {selectedCar && (
        <div className="admin-modal admin-modal--scroll">
          <div className="admin-modal__panel admin-modal__panel--wide">
            <div className="admin-modal__head">
              <div>
                <h1 className="admin-modal__title" style={{ fontFamily: 'var(--font-plate)', textTransform: 'none', letterSpacing: '0.06em' }}>
                  {selectedCar.licensePlate}
                </h1>
                <span className={`status-badge ${getStatusColor(selectedCar.status)}`} style={{ marginTop: 8, display: 'inline-block' }}>
                  {getStatusText(selectedCar.status)}
                </span>
              </div>
              <button type="button" onClick={() => setSelectedCar(null)} className="admin-modal__close" aria-label="Закрыть">
                <XCircle size={20} />
              </button>
            </div>

            <div className="admin-modal__body">
              <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div className="meta-item">
                  <Calendar size={16} />
                  {new Date(selectedCar.createdAt).toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {selectedCar.location?.address && (
                  <div className="meta-item">
                    <MapPin size={16} />
                    {selectedCar.location.address}
                  </div>
                )}
                {selectedCar.submittedBy && (
                  <div className="meta-item">
                    <User size={16} />
                    От: {selectedCar.submittedBy}
                  </div>
                )}
              </div>

              {selectedCar.description && (
                <div style={{ marginBottom: 24 }}>
                  <h3 className="admin-section__title" style={{ fontSize: 18, marginBottom: 8 }}>Описание</h3>
                  <p style={{ color: 'var(--slate)', lineHeight: 1.6 }}>{selectedCar.description}</p>
                </div>
              )}

              {selectedCar.photos && selectedCar.photos.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 className="admin-section__title" style={{ fontSize: 18, marginBottom: 12 }}>
                    Фотографии ({selectedCar.photos.length})
                  </h3>
                  <div className="admin-media-grid">
                    {selectedCar.photos.map((photo, index) => (
                      <img
                        key={index}
                        src={`${config.API_URL}/uploads/${photo}`}
                        alt={`Фото ${index + 1}`}
                        onClick={() => {
                          setLightboxIndex(index);
                          setLightboxOpen(true);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {selectedCar.videos && selectedCar.videos.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 className="admin-section__title" style={{ fontSize: 18, marginBottom: 12 }}>
                    Видео ({selectedCar.videos.length})
                  </h3>
                  <div className="admin-media-grid admin-media-grid--video">
                    {selectedCar.videos.map((video, index) => {
                      const mediaIndex = (selectedCar.photos?.length || 0) + index;
                      return (
                        <button
                          key={index}
                          type="button"
                          className="media-thumb media-thumb--video"
                          onClick={() => {
                            setLightboxIndex(mediaIndex);
                            setLightboxOpen(true);
                          }}
                          style={{ border: 'none', padding: 0 }}
                        >
                          <video
                            src={`${config.API_URL}/uploads/${video}`}
                            muted
                            preload="metadata"
                            playsInline
                          />
                          <span className="media-thumb__play">▶</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedCar.photos.length === 0 && selectedCar.videos.length === 0 && (
                <div className="admin-empty">
                  <Car size={48} />
                  <p>Нет медиафайлов</p>
                </div>
              )}

              {selectedCar.status === 'ocr_review' && (
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                  <OcrResolveForm car={selectedCar} onResolve={resolveOcr} />
                </div>
              )}

              {selectedCar.status === 'pending' && (
                <div className="admin-modal__actions" style={{ paddingTop: 20, borderTop: '1px solid var(--line)' }}>
                  <button
                    type="button"
                    onClick={() => moderateCar(selectedCar._id, 'approved')}
                    className="btn btn-success"
                  >
                    <CheckCircle size={16} />
                    Одобрить заявку
                  </button>
                  <button
                    type="button"
                    onClick={() => moderateCar(selectedCar._id, 'rejected')}
                    className="btn btn-danger"
                  >
                    <XCircle size={16} />
                    Отклонить заявку
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedCar && (
        <MediaLightbox
          open={lightboxOpen}
          index={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={setLightboxIndex}
          items={[
            ...(selectedCar.photos || []).map((photo, i) => ({
              type: 'image',
              src: `${config.API_URL}/uploads/${photo}`,
              alt: `Фото ${i + 1}`
            })),
            ...(selectedCar.videos || []).map((video, i) => ({
              type: 'video',
              src: `${config.API_URL}/uploads/${video}`,
              alt: `Видео ${i + 1}`
            }))
          ]}
        />
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <div className="admin-modal">
          <div className="admin-modal__panel" style={{ maxWidth: 420 }}>
            <div className="admin-modal__head">
              <h3 className="admin-modal__title">Создать администратора</h3>
              <button type="button" onClick={() => setShowCreateAdmin(false)} className="admin-modal__close" aria-label="Закрыть">
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={createAdmin}>
              <div>
                <label className="admin-field-label">Имя пользователя</label>
                <input
                  type="text"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  className="form-input"
                  placeholder="Введите имя пользователя"
                  required
                />
              </div>
              <div>
                <label className="admin-field-label">Пароль</label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="form-input"
                  placeholder="Введите пароль"
                  required
                />
              </div>
              <div className="admin-modal__actions">
                <button type="submit" className="btn btn-primary">
                  Создать администратора
                </button>
                <button type="button" onClick={() => setShowCreateAdmin(false)} className="btn btn-secondary">
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Рекламная форма */}
      {showAdForm && (
        <div className="admin-modal">
          <div className="admin-modal__panel admin-modal__panel--md">
            <div className="admin-modal__head">
              <h3 className="admin-modal__title">
                {editingAd ? 'Редактировать рекламу' : 'Добавить рекламу'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowAdForm(false);
                  setEditingAd(null);
                  setNewAd({
                    title: '',
                    link: '',
                    position: 'left',
                    priority: 0,
                    startDate: '',
                    endDate: '',
                    isActive: true,
                    mediaType: 'image',
                    betweenSettings: {
                      frequency: 3,
                      startPosition: 1,
                      maxPerPage: 5
                    }
                  });
                  setAdMedia(null);
                }}
                className="admin-modal__close"
                aria-label="Закрыть"
              >
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={handleAdSubmit}>
              <div className="admin-form-grid">
                <div>
                  <label className="admin-field-label">Название рекламы</label>
                  <input
                    type="text"
                    value={newAd.title}
                    onChange={(e) => setNewAd({ ...newAd, title: e.target.value })}
                    className="form-input"
                    placeholder="Введите название"
                    required
                  />
                </div>
                <div>
                  <label className="admin-field-label">Ссылка</label>
                  <input
                    type="url"
                    value={newAd.link}
                    onChange={(e) => setNewAd({ ...newAd, link: e.target.value })}
                    className="form-input"
                    placeholder="https://example.com"
                    required
                  />
                </div>
              </div>

              <div className="admin-form-grid">
                <div>
                  <label className="admin-field-label">Позиция / формат</label>
                  <select
                    value={newAd.position}
                    onChange={(e) => setNewAd({ ...newAd, position: e.target.value })}
                    className="form-input"
                    required
                  >
                    {AD_POSITIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.labelRu} — {p.size}
                      </option>
                    ))}
                  </select>
                  {getAdPositionMeta(newAd.position) && (
                    <div className="ad-format-hint">
                      <strong>{getAdPositionMeta(newAd.position).size}</strong>
                      {' · '}
                      {getAdPositionMeta(newAd.position).hint}
                      <br />
                      {getAdPositionMeta(newAd.position).accept}
                    </div>
                  )}
                </div>
                <div>
                  <label className="admin-field-label">Приоритет</label>
                  <input
                    type="number"
                    value={newAd.priority}
                    onChange={(e) => setNewAd({ ...newAd, priority: parseInt(e.target.value) || 0 })}
                    className="form-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              {newAd.position === 'between' && (
                <div style={{
                  background: 'var(--paper-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  padding: 16
                }}>
                  <h4 className="admin-section__title" style={{ fontSize: 16, marginBottom: 14 }}>
                    Настройки размещения между постами
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                    <div>
                      <label className="admin-field-label">Частота показа</label>
                      <input
                        type="number"
                        value={newAd.betweenSettings?.frequency || 3}
                        onChange={(e) => setNewAd({
                          ...newAd,
                          betweenSettings: {
                            ...(newAd.betweenSettings || {}),
                            frequency: parseInt(e.target.value) || 3
                          }
                        })}
                        className="form-input"
                        placeholder="3"
                        min="1"
                        max="20"
                      />
                      <p className="admin-table__muted">Через каждые N постов</p>
                    </div>
                    <div>
                      <label className="admin-field-label">Начальная позиция</label>
                      <input
                        type="number"
                        value={newAd.betweenSettings?.startPosition || 1}
                        onChange={(e) => setNewAd({
                          ...newAd,
                          betweenSettings: {
                            ...(newAd.betweenSettings || {}),
                            startPosition: parseInt(e.target.value) || 1
                          }
                        })}
                        className="form-input"
                        placeholder="1"
                        min="1"
                      />
                      <p className="admin-table__muted">С N-го поста</p>
                    </div>
                    <div>
                      <label className="admin-field-label">Максимум на странице</label>
                      <input
                        type="number"
                        value={newAd.betweenSettings?.maxPerPage || 5}
                        onChange={(e) => setNewAd({
                          ...newAd,
                          betweenSettings: {
                            ...(newAd.betweenSettings || {}),
                            maxPerPage: parseInt(e.target.value) || 5
                          }
                        })}
                        className="form-input"
                        placeholder="5"
                        min="1"
                        max="20"
                      />
                      <p className="admin-table__muted">Макс. рекламы</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="admin-form-grid">
                <div>
                  <label className="admin-field-label">Начало показа</label>
                  <input
                    type="datetime-local"
                    value={newAd.startDate}
                    onChange={(e) => setNewAd({ ...newAd, startDate: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="admin-field-label">Окончание показа</label>
                  <input
                    type="datetime-local"
                    value={newAd.endDate}
                    onChange={(e) => setNewAd({ ...newAd, endDate: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="admin-field-label">Тип медиа</label>
                <select
                  value={newAd.mediaType}
                  onChange={(e) => setNewAd({...newAd, mediaType: e.target.value})}
                  className="form-input"
                >
                  <option value="image">Изображение (JPG, PNG)</option>
                  <option value="gif">GIF анимация</option>
                  <option value="video">Видео (MP4, MOV, AVI)</option>
                </select>
              </div>

              <label className="admin-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={newAd.isActive}
                  onChange={(e) => setNewAd({...newAd, isActive: e.target.checked})}
                  style={{ margin: 0 }}
                />
                Активная реклама
              </label>

              <div>
                <label className="admin-field-label">Медиа файл {!editingAd && '(обязательно)'}</label>
                <input
                  type="file"
                  accept={newAd.mediaType === 'video' ? 'video/*' : 'image/*'}
                  onChange={(e) => setAdMedia(e.target.files[0])}
                  className="form-input"
                  required={!editingAd}
                />
                {editingAd && (editingAd.image || editingAd.video) && (
                  <div style={{ marginTop: 8 }}>
                    <p className="admin-table__muted">Текущий медиа файл:</p>
                    {editingAd.mediaType === 'video' ? (
                      <video
                        src={`${config.API_URL}/uploads/${editingAd.video}`}
                        style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 2 }}
                        controls
                      />
                    ) : (
                      <img
                        src={`${config.API_URL}/uploads/${editingAd.image || editingAd.video}`}
                        alt="Текущий медиа файл"
                        style={{ width: 100, height: 60, objectFit: 'cover', borderRadius: 2 }}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="admin-modal__actions">
                <button type="submit" className="btn btn-primary">
                  {editingAd ? 'Обновить рекламу' : 'Создать рекламу'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAdForm(false);
                    setEditingAd(null);
                    setNewAd({
                      title: '',
                      link: '',
                      position: 'left',
                      priority: 0,
                      startDate: '',
                      endDate: '',
                      isActive: true,
                      mediaType: 'image',
                      betweenSettings: {
                        frequency: 3,
                        startPosition: 1,
                        maxPerPage: 5
                      }
                    });
                    setAdMedia(null);
                  }}
                  className="btn btn-secondary"
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно для дубликатов */}
      {showDuplicates && (
        <div className="admin-modal admin-modal--scroll">
          <div className="admin-modal__panel admin-modal__panel--wide" style={{ padding: 24, minHeight: 'auto' }}>
            <div className="admin-modal__head" style={{ padding: 0, border: 'none', marginBottom: 20 }}>
              <div>
                <h3 className="admin-modal__title">Дубликаты записей</h3>
                <p className="admin-modal__lead">
                  Найдено {duplicates.length} дубликат{duplicates.length > 1 ? 'ов' : ''} для номера {selectedCar?.licensePlate}
                </p>
              </div>
              <button type="button" onClick={() => setShowDuplicates(false)} className="admin-modal__close" aria-label="Закрыть">
                <X size={20} />
              </button>
            </div>

            {duplicates.length === 0 ? (
              <div className="admin-empty">
                <AlertTriangle size={48} color="#6b7385" />
                <h3>Дубликаты не найдены</h3>
                <p>Для этой записи не найдено дубликатов</p>
              </div>
            ) : (
              <div className="admin-cars">
                {duplicates.map((duplicate) => (
                  <div key={duplicate._id} className="car-card">
                    <div className="car-card-header">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                        <div>
                          <h4 className="admin-car__plate">{duplicate.licensePlate}</h4>
                          <span className={`status-badge ${getStatusColor(duplicate.status)}`}>
                            {getStatusText(duplicate.status)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => mergeCars(selectedCar._id, duplicate._id)}
                            className="btn btn-primary"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            title="Объединить с основной записью"
                          >
                            <GitMerge size={14} />
                            Объединить
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!selectedCar) return;
                              const reason = window.prompt('Причина отклонения дубликата:') || '';
                              if (reason !== null) {
                                rejectDuplicate(selectedCar._id, duplicate._id, reason);
                              }
                            }}
                            className="btn btn-danger"
                            style={{ padding: '8px 12px', fontSize: 13 }}
                            title="Отклонить дубликат"
                          >
                            <X size={14} />
                            Отклонить
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="car-card-body">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div className="meta-item">
                          <Calendar size={16} />
                          {new Date(duplicate.createdAt).toLocaleDateString('ru-RU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {duplicate.submittedBy && (
                          <div className="meta-item">
                            <User size={16} />
                            От: {duplicate.submittedBy}
                          </div>
                        )}
                        {duplicate.description && (
                          <p style={{
                            fontSize: 14,
                            color: 'var(--slate)',
                            lineHeight: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            margin: 0
                          }}>
                            {duplicate.description}
                          </p>
                        )}
                        <div style={{ display: 'flex', gap: 16 }}>
                          {duplicate.photos && duplicate.photos.length > 0 && (
                            <div className="meta-item"><Image size={16} />{duplicate.photos.length} фото</div>
                          )}
                          {duplicate.videos && duplicate.videos.length > 0 && (
                            <div className="meta-item"><Video size={16} />{duplicate.videos.length} видео</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно для настройки водных знаков */}
      {showWatermarkModal && (
        <div className="admin-modal">
          <div className="admin-modal__panel admin-modal__panel--md">
            <div className="admin-modal__head">
              <div>
                <h3 className="admin-modal__title">Настройки водного знака</h3>
                <p className="admin-modal__lead">
                  Выберите настройки для водного знака или одобрите без водного знака
                </p>
              </div>
              <button type="button" onClick={() => setShowWatermarkModal(false)} className="admin-modal__close" aria-label="Закрыть">
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <label className="admin-field-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 0, fontSize: 15 }}>
                <input
                  type="checkbox"
                  checked={watermarkSettings.applyWatermark}
                  onChange={(e) => setWatermarkSettings({...watermarkSettings, applyWatermark: e.target.checked})}
                  style={{ margin: 0 }}
                />
                Применить водный знак к медиа файлам
              </label>

              {watermarkSettings.applyWatermark && (
                <div style={{
                  background: 'var(--paper-2)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius)',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14
                }}>
                  <div>
                    <label className="admin-field-label">Текст водного знака</label>
                    <input
                      type="text"
                      value={watermarkSettings.watermarkText}
                      onChange={(e) => setWatermarkSettings({...watermarkSettings, watermarkText: e.target.value})}
                      className="form-input"
                      placeholder="Avto-Tarix.uz"
                    />
                  </div>
                  <div>
                    <label className="admin-field-label">Позиция</label>
                    <select
                      value={watermarkSettings.watermarkPosition}
                      onChange={(e) => setWatermarkSettings({...watermarkSettings, watermarkPosition: e.target.value})}
                      className="form-input"
                    >
                      <option value="bottom-right">Справа снизу</option>
                      <option value="bottom-left">Слева снизу</option>
                      <option value="top-right">Справа сверху</option>
                      <option value="top-left">Слева сверху</option>
                      <option value="center">По центру</option>
                    </select>
                  </div>
                  <div>
                    <label className="admin-field-label">Прозрачность: {Math.round(watermarkSettings.watermarkOpacity * 100)}%</label>
                    <input
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.1"
                      value={watermarkSettings.watermarkOpacity}
                      onChange={(e) => setWatermarkSettings({...watermarkSettings, watermarkOpacity: parseFloat(e.target.value)})}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              )}

              <div className="admin-modal__actions">
                <button type="button" onClick={handleWatermarkApproval} className="btn btn-primary">
                  <CheckCircle size={16} />
                  Одобрить с водным знаком
                </button>
                <button type="button" onClick={handleApprovalWithoutWatermark} className="btn btn-secondary">
                  Одобрить без водного знака
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminPanel;